import { Box, useTheme } from '@mui/material'
import * as React from 'react'
import { useEffect, useState } from 'react'
import { A11y, Autoplay, Navigation, Pagination, Virtual } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Swiper as SwiperType } from 'swiper/types'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import VodItem from '@/features/VOD/vodClasses/vodItem'
import { pageType } from '@/shared/store/types'
import { useVodItemsContext } from '@/features/VOD/context/UseGetVodItemContext/useVodItemsContext'
import { ZeroRowItemView } from './ZeroRowItemView'
import { debounceSpeedShort } from '@/shared/constants'
import { debounce, isSmartTV } from '@/shared/lib'
import {
    setFocus,
    useFocusable,
} from '@noriginmedia/norigin-spatial-navigation'
import { ROW_ZERO_FOCUS_KEY } from '@/shared/constants/focusLibraryConstants'

interface ISlider {
    buttonHeight?: number
    vodItemHeight: number
    handleGoToCategoryButtonClick: (
        itemId: string,
        title: string,
        type: pageType
    ) => void
    scrollPosition: {
        x: number
        y: number
    }
    hideCaptions?: boolean
    hideProgress?: boolean
    rowHeightPercentage?: number
    cardRadiusDp?: number
    isHomeContentRow?: boolean
}

export const ZeroRowVodSlider = ({
    vodItemHeight,
    handleGoToCategoryButtonClick,
    scrollPosition,
    hideCaptions,
    hideProgress,
    rowHeightPercentage,
    cardRadiusDp,
    isHomeContentRow,
}: ISlider) => {
    const theme = useTheme()
    const [swipe, setSwipe] = useState<SwiperType>()
    const [activeIndex, setActiveIndex] = useState(0)
    const { rowItems } = useVodItemsContext()

    const handleSlideChange = () => {
        if (swipe) {
            setActiveIndex(swipe.activeIndex)
        }
    }

    const leftClick = debounce(() => {
        swipe?.slidePrev()
        handleSlideChange()
    }, debounceSpeedShort)

    const rightClick = debounce(() => {
        swipe?.slideNext()
        handleSlideChange()
    }, debounceSpeedShort)

    const { ref, focusSelf, focused } = useFocusable({
        focusKey: ROW_ZERO_FOCUS_KEY,
        focusable: !!isSmartTV(),
        forceFocus: true,

        onFocus: () => {
            ref?.current?.focus()
        },

        onArrowPress: (direction) => {
            if (direction === 'left') {
                leftClick()
                return false
            }
            if (direction === 'right') {
                rightClick()
                return false
            }
            if (direction === 'up') {
                setFocus('MenuButtons') // Explicitly set focus to MenuButtons
                return false // Prevent default behavior
            }
            return true
        },
    })

    useEffect(() => {
        if (focused && ref.current) {
            ref.current.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
                inline: 'nearest',
            })
        }
    }, [focused])

    useEffect(() => {
        swipe?.on('slideChange', handleSlideChange)

        focusSelf()
        handleSlideChange()

        return () => {
            swipe?.off('slideChange', handleSlideChange)
        }
    }, [swipe])

    useEffect(() => {
        focusSelf()
    }, [focusSelf])

    // const LeftArrowWrapper = {
    //     position: 'absolute',
    //     left: 0,
    //     top: 0,
    //     width: {
    //         xs: '50%',
    //         md: '50%',
    //     },
    //     height: '50%',
    //     pointerEvents: 'all',
    //     cursor: 'pointer',
    // }

    // const RightArrowWrapper = {
    //     position: 'absolute',
    //     right: 0,
    //     top: 0,
    //     width: {
    //         xs: '50%',
    //         md: '50%',
    //     },
    //     height: '50%',
    //     pointerEvents: 'all',
    //     cursor: 'pointer',
    // }
    //
    // const shadowBoxStyle = {
    //     position: 'absolute',
    //     width: '100%',
    //     height: '100%',
    //     background: 'black',
    //     opacity: 0.3,
    //     top: 0,
    //     left: 0,
    //     zIndex: 8,
    //     pointerEvents: 'all',
    //     cursor: 'pointer',
    // }

    if (!rowItems.length) return <></>

    return (
        <Box
            position="relative"
            ref={ref}
            sx={{
                border: focused && !!isSmartTV() ? '5px solid red' : 'none',
            }}>
            <Swiper
                onBeforeInit={(swiper: SwiperType) => {
                    setSwipe(swiper)
                }}
                onPaginationRender={(swiper: SwiperType) => {
                    const bullets = swiper.pagination.bullets as HTMLElement[]
                    if (bullets.length) {
                        const isActiveBulletSet = bullets.some((bullet) =>
                            bullet.classList.contains(
                                'swiper-pagination-bullet-active'
                            )
                        )

                        if (!isActiveBulletSet) {
                            bullets.forEach(
                                (bullet) =>
                                    (bullet.style.backgroundColor =
                                        theme.palette.custom.lightGray)
                            )
                            bullets[0].classList.add(
                                'swiper-pagination-bullet-active'
                            )
                            bullets[0].style.backgroundColor =
                                theme.palette.primary.main
                        }
                    }
                    bullets?.forEach((bullet) => {
                        if (bullet.className.includes('active')) {
                            bullet.style.backgroundColor =
                                theme.palette.primary.main
                        } else {
                            bullet.style.backgroundColor =
                                theme.palette.custom.lightGray
                        }
                    })
                }}
                modules={[Navigation, Pagination, A11y, Autoplay, Virtual]}
                pagination={{
                    clickable: true,
                    horizontalClass: 'swiper-pagination-custom',
                }}
                autoplay={{
                    delay: 3000,
                    disableOnInteraction: false,
                }}
                virtual={{ addSlidesBefore: 0, addSlidesAfter: 0 }}
                loop
                spaceBetween={4}
                slidesPerGroup={1}
                slidesPerView={1}>
                {rowItems.slice(0, 10).map((item: VodItem, index) => (
                    <SwiperSlide
                        key={item.id || index}
                        style={{ width: 'min-content' }}>
                        <ZeroRowItemView
                            hideCaptions={hideCaptions}
                            hideProgress={hideProgress}
                            vodItemHeight={vodItemHeight}
                            cardRadiusDp={cardRadiusDp}
                            rowHeightPercentage={rowHeightPercentage}
                            item={item}
                            handleGoToCategoryButtonClick={
                                handleGoToCategoryButtonClick
                            }
                            scrollPosition={scrollPosition}
                            isHomeContentItem={isHomeContentRow}
                            isActive={activeIndex === index + 1}
                            focused={focused}
                        />
                    </SwiperSlide>
                ))}

                {/*{rowItems.length && !isSmartTV() && (*/}
                {/*    <Box position="absolute" top="78%" right="0px" width="10%">*/}
                {/*        <Box sx={LeftArrowWrapper} onClick={leftClick}>*/}
                {/*            <Arrow icon={<VodArrowLeft />} />*/}
                {/*            <Box sx={shadowBoxStyle} />*/}
                {/*        </Box>*/}

                {/*        <Box sx={RightArrowWrapper} onClick={rightClick}>*/}
                {/*            <Arrow icon={<VodArrowRight />} />*/}
                {/*            <Box sx={shadowBoxStyle} />*/}
                {/*        </Box>*/}
                {/*    </Box>*/}
                {/*)}*/}
            </Swiper>
        </Box>
    )
}
